import z from 'zod'
import { Model } from '../../_kernel/architecture'
import { ID } from '../../_kernel/ID'
import { ListOfFacility } from '../../shared/domain/ListOfFacility'
import { ListOfImage } from '../../_kernel/ListOfImage'
import { Name } from '../../_kernel/Name'
import { Pax } from '../../shared/domain/Pax'
import { Allotment } from './Allotment'
import { RoomNumber } from '../../_kernel/RoomNumber'

export const NO_ROOM_IMAGE_URL =
  'https://static.stayforlong.com/web/images/hotel-detail-page/rooms/20260220-no-room-image.webp'

export const SINGLE_ROOM = 1

export const RoomValidation = z.object({
  id: z.instanceof(ID, {
    message: '[Room] id is required',
  }),
  name: z.instanceof(Name, { message: '[Room] name is required' }),
  pax: z.instanceof(Pax, { message: '[Room] pax is required' }),
  allotment: z.instanceof(Allotment, { message: '[Room] allotment is required' }),
  facilities: z.instanceof(ListOfFacility).optional(),
  images: z.instanceof(ListOfImage).optional(),
  roomsNumber: z.instanceof(RoomNumber).optional(),
})

export class Room extends Model {
  static create({
    id,
    name,
    pax,
    allotment,
    facilities,
    images,
    roomsNumber,
  }: z.infer<typeof RoomValidation>) {
    RoomValidation.parse({ id, name, pax, allotment, facilities, images, roomsNumber })
    return new Room(
      id,
      name,
      pax,
      allotment,
      facilities ?? ListOfFacility.empty(),
      images ?? ListOfImage.empty(),
      roomsNumber ?? RoomNumber.create({ value: SINGLE_ROOM })
    )
  }

  static fromJSON(
    json: ReturnType<Room['toJSON']> & {
      name: string
      facilities?: ReturnType<ListOfFacility['toJSON']>
      images?: ReturnType<ListOfImage['toJSON']>
    }
  ) {
    return Room.create({
      id: ID.create({ value: json.id }),
      name: Name.create({ value: json.name }),
      pax: Pax.fromJSON(json.pax),
      allotment: Allotment.create({ value: json.allotment }),
      facilities: json.facilities
        ? ListOfFacility.fromJSON({ json: json.facilities })
        : ListOfFacility.fromJSONOrEmpty({ json: undefined }),
      images: json.images
        ? ListOfImage.fromJSON({ json: json.images })
        : ListOfImage.create({ list: [] }),
      roomsNumber: RoomNumber.fromNumber({ value: json.roomsNumber }),
    })
  }

  constructor(
    private readonly _id: ID,
    private readonly _name: Name,
    private readonly _pax: Pax,
    private readonly _allotment: Allotment,
    private readonly _facilities: ListOfFacility,
    private readonly _images: ListOfImage,
    private readonly _roomsNumber: RoomNumber
  ) {
    super()
  }

  roomID() {
    return this._id
  }

  name() {
    return this._name.value
  }

  nameCapitalized() {
    const nameValue = this._name.value
    return nameValue.at(0)?.toUpperCase() + nameValue.slice(1)
  }

  pax() {
    return this._pax
  }

  adults() {
    return this.pax().adultsCount()
  }

  isMoreThanOneAdult() {
    return this.pax().isMoreThanOneAdult()
  }

  children() {
    return this.pax().childrenCount()
  }

  isMoreThanOneChild() {
    return this.pax().isMoreThanOneChild()
  }

  isAtLeastOneChild() {
    return this.pax().isAtLeastOneChild()
  }

  allotment() {
    return this._allotment.value
  }

  isLastRoom() {
    return this._allotment.isTheLastOne()
  }

  facilities() {
    return this._facilities
  }

  featuredFacilities(maxFacilities?: number) {
    return this._facilities.featured(maxFacilities)
  }

  images() {
    return this._images
  }

  roomsNumber() {
    return this._roomsNumber.value
  }

  isMultiRoom() {
    return this.roomsNumber() > SINGLE_ROOM
  }

  firstImage() {
    return this._images.at(0)
  }

  imagesCount() {
    return this._images.count()
  }

  hasImages() {
    return this.imagesCount() > 1
  }

  imagePlaceholder() {
    return {
      images: [NO_ROOM_IMAGE_URL],
    }
  }

  toJSON() {
    return {
      id: this._id.value,
      name: this._name.value,
      pax: this._pax.toJSON(),
      allotment: this._allotment.value,
      facilities: this._facilities.toJSON(),
      images: this._images.toJSON(),
      roomsNumber: this._roomsNumber.value,
    }
  }
}
